import { Injectable } from '@angular/core';
import { ContentService } from '@blk/cwp-ng-framework';
import { ErrorDCR } from '@ddt-web/config';
import { WebUserDataConstants } from '@ddt-web/domain';
import { Config, StoreService } from '@ddt-web/store-service';
import { PartnerConstants } from '@ddt-web/user-data';
import * as get from 'lodash/get';
import * as has from 'lodash/has';

import { PartnerErrorHelper } from './partner-error-helper';

@Injectable({ providedIn: 'root' })
export class PartnerStoreHelper {
	constructor(
		private storeService: StoreService,
		private partnerErrorHelper: PartnerErrorHelper,
		private contentService: ContentService
	) { }

	/**
	 * Function to populate the provied store config with portfolio details data.
	 * If response is not valid, the config store is updated with an error object and an error is thrown.
	 *
	 * @param {*} configs array of configs to store the data
	 * @param {*} response an array of flattened responses
	 * @param {string[]} externalModelIds list of partner ids
	 * @param {ContentService} contentService the content service for error reporting
	 * @memberof PartnerPortfolioDataService
	 */
	public updateStoreFromExternalResponse(configs: any[], response: any[], externalModelIds: string[]): void {
		let foundOtherIdentifier = false;

		// Create a map of {id: response}
		const idsResponseMap = response.reduce((existingMap, currentResponse) => {
			let id = get(currentResponse, 'portfolioId') || get(currentResponse, 'id') || get(currentResponse, 'uniqueId');

			// Get ID from otherIdentifiers object
			const identifierObj = get(currentResponse, 'otherIdentifiers', [])
				.find(otherIdentifier => get(otherIdentifier, 'identifierType') === WebUserDataConstants.IDENTIFIER_TYPE.CUSTOM);
			if (has(identifierObj, 'identifier')) {
				const otherId = PartnerConstants.PARTNER.PARTNER_IDENTIFIER + identifierObj['identifier'];
				if (externalModelIds.includes(otherId)) {
					id = otherId;
					foundOtherIdentifier = true;
				}
			}

			return Object.assign(existingMap, { [id]: Object.assign({}, currentResponse, { id }) });
		}, {});

		if (response.some(portfolioResponse => !portfolioResponse)) {
			configs.forEach(config => this.storeService.upsertMany(config, Object.keys(idsResponseMap).map(id => this.partnerErrorHelper.createErrorObject(id))));
			// throw error after update store, so components still render when not all data returned
			this.partnerErrorHelper.handleResponseError(
				response,
				this.contentService.get('PARTNER_NO_PROGRAMS_TARGET_RISK_ERROR', ErrorDCR.PARTNER_NO_PROGRAMS_TARGET_RISK_ERROR)
			);
		} else if (idsResponseMap) {
			if (foundOtherIdentifier) {
				configs.forEach(config => this.storeService.upsertMany(config, Object.values(idsResponseMap)));
			} else {
				configs.forEach(config => this.storeService.upsertMany(config, Object.keys(idsResponseMap).map(id => this.partnerErrorHelper.createErrorObject(id))));
			}
		}
	}

	/**
	 * Update the provided stores with the response. The response is expected to be as an array.
	 * If response is not valid, the config store is updated with empty array and an error is thrown.
	 *
	 * @param configs the array of config store
	 * @param response the api call response
	 * @param responseJson used to handle the response error
	 * @param skipErrorHandler default to false
	 *
	 */
	public updateStoreFromResponseList<T>(configs: Array<Config<T>>, response: any, responseJson: any, skipErrorHandler: boolean = false): void {
		if (response) {
			const portToAddArray = [];
			response.forEach(portfolio => {
				const portToAdd = Object.assign({ ...portfolio }, { id: String(portfolio.portfolioId) });
				portToAddArray.push(portToAdd);
			});

			configs.forEach(config => this.storeService.upsertMany(config, portToAddArray));
		} else {
			// throw error after update store, so components still render when not all data returned
			configs.forEach(config => this.storeService.upsertMany(config, []));
			if (!skipErrorHandler) {
				this.partnerErrorHelper.handleResponseError(responseJson, this.contentService.get('PARTNER_NO_MODEL_LIST_ERROR', ErrorDCR.PARTNER_NO_MODEL_LIST_ERROR));
			}
		}
	}

	/**
	 * Function to populate partnerConfig with data.
	 * If response is not valid, the config store is updated with empty array and an error is thrown.
	 *
	 * @param {Array<Config>} configs
	 * @param {*} response
	 * @param {string} partner
	 * @param {string} handleType
	 * @param {*} [responseJson]
	 * @param {boolean} [skipErrorHandler=false]
	 * @memberof PartnerPortfolioDataService
	 */
	public updateStoreFromHandleResponse<T>(
		configs: Array<Config<T>>,
		response: any,
		handleType: string,
		responseJson?: any,
		skipErrorHandler: boolean = false
	): void {
		if (response) {
			const partnerConfig = Object.assign({ ...response }, { id: handleType });
			configs.forEach(config => this.storeService.upsertOne(config, partnerConfig));
		} else {
			// throw error after update store, so components still render when not all data returned
			configs.forEach(config => this.storeService.upsertMany(config, []));
			if (!skipErrorHandler) {
				this.partnerErrorHelper.handleResponseError(
					responseJson,
					this.contentService.get('PARTNER_NO_PROGRAMS_TARGET_RISK_ERROR', ErrorDCR.PARTNER_NO_PROGRAMS_TARGET_RISK_ERROR)
				);
			}
		}
	}
}
